
import React from 'react';

/**
 * FileSpread Brand Logo
 * High-fidelity SVG recreation of the provided brand icon.
 * Features a dark squircle background, orange gradient WiFi arcs, a central file icon, and a signature swoosh.
 */
export const Logo = ({ size = 32, className = "" }: { size?: number; className?: string }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 1024 1024" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg" 
    className={`${className} drop-shadow-lg`}
  >
    <defs>
      <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFC837" />
        <stop offset="50%" stopColor="#FF8008" />
        <stop offset="100%" stopColor="#FF3D00" />
      </linearGradient>
      <filter id="icon-glow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="12" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>
    
    {/* Squircle Background */}
    <rect x="64" y="64" width="896" height="896" rx="200" fill="#0A0812" />
    
    <g filter="url(#icon-glow)">
      {/* Top Arc */}
      <path 
        d="M280 420C400 290 624 290 744 420" 
        stroke="url(#logo-gradient)" 
        strokeWidth="70" 
        strokeLinecap="round" 
      />
      
      {/* Middle Arc */}
      <path 
        d="M360 520C420 460 604 460 664 520" 
        stroke="url(#logo-gradient)" 
        strokeWidth="60" 
        strokeLinecap="round" 
      />
      
      {/* File Base */}
      <path 
        d="M440 400H560C571.046 400 580 408.954 580 420V740C580 751.046 571.046 760 560 760H440C428.954 760 420 751.046 420 740V420C420 408.954 428.954 400 440 400Z" 
        fill="url(#logo-gradient)" 
      />
      
      {/* File Fold Detail */}
      <path d="M530 400V450H580L530 400Z" fill="#0A0812" />
      
      {/* S-Swoosh Wrap */}
      <path 
        d="M380 480C380 480 440 440 512 560C584 680 644 640 644 640" 
        stroke="url(#logo-gradient)" 
        strokeWidth="60" 
        strokeLinecap="round" 
        fill="none" 
      />
      
      {/* Bottom Dot */}
      <circle cx="512" cy="850" r="45" fill="url(#logo-gradient)" />
    </g>
  </svg>
);

/**
 * Dhakad Labs Brand Logo
 * High-fidelity SVG recreation of the "D-L" neon logo.
 * Features a glowing border, neon text, and a heart badge.
 */
export const DhakadLabsLogo = ({ size = 80, className = "" }: { size?: number; className?: string }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 1024 1024" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg" 
    className={`${className}`}
  >
    <defs>
      <linearGradient id="border-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00F2FF" />
        <stop offset="100%" stopColor="#7000FF" />
      </linearGradient>
      <filter id="neon-glow" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="15" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    {/* Squircle Background */}
    <rect x="180" y="180" width="664" height="664" rx="140" fill="#0D0B14" />
    
    {/* Glowing Border */}
    <rect 
      x="180" y="180" width="664" height="664" rx="140" 
      stroke="url(#border-gradient)" 
      strokeWidth="12" 
      filter="url(#neon-glow)"
    />

    {/* D-L Text */}
    <text 
      x="512" y="550" 
      textAnchor="middle" 
      fill="url(#border-gradient)" 
      style={{ 
        fontFamily: 'Manrope, sans-serif', 
        fontSize: '240px', 
        fontWeight: '800', 
        filter: 'url(#neon-glow)',
        letterSpacing: '-10px'
      }}
    >
      D-L
    </text>

    {/* Bottom Right Heart Badge */}
    <g transform="translate(710, 710)">
      <circle cx="0" cy="0" r="70" fill="#FF5E8E" />
      <path 
        d="M0 35 C-15 25 -30 10 -30 -10 C-30 -25 -20 -35 -10 -35 C-4 -35 0 -30 0 -30 C0 -30 4 -35 10 -35 C20 -35 30 -25 30 -10 C30 10 15 25 0 35" 
        fill="white" 
        transform="scale(0.8) translate(0, 5)"
      />
    </g>
  </svg>
);

// Added className and size support to DeviceIcon to resolve TypeScript errors
export const DeviceIcon = ({ type, size = 24, className = "" }: { type: string; size?: number; className?: string }) => {
  if (type === 'mobile') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
        <line x1="12" y1="18" x2="12.01" y2="18"></line>
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
      <line x1="2" y1="20" x2="22" y2="20"></line>
    </svg>
  );
};

export const WifiIcon = ({ size = 20, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
    <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
    <line x1="12" y1="20" x2="12.01" y2="20"></line>
  </svg>
);

export const PinIcon = ({ size = 16, smSize = 18, filled = false, className = "" }: { size?: number; smSize?: number; filled?: boolean; className?: string }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill={filled ? "currentColor" : "none"} 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={`${className} w-[16px] h-[16px] sm:w-[18px] sm:h-[18px]`}
  >
    <path d="M12 2v8"></path>
    <path d="m16.4 13.5-4.4-4.4-4.4 4.4"></path>
    <path d="M12 22v-8"></path>
    <path d="m7.6 10.5 4.4 4.4 4.4-4.4"></path>
  </svg>
);

export const HistoryIcon = ({ size = 20, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
    <path d="M3 3v5h5"></path>
    <path d="M12 7v5l4 2"></path>
  </svg>
);

// Added className and size support to SendIcon to resolve TypeScript errors
export const SendIcon = ({ size = 20, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

// Added className and size support to FileIcon to resolve TypeScript errors
export const FileIcon = ({ size = 20, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
    <polyline points="13 2 13 9 20 9"></polyline>
  </svg>
);

export const LinkIcon = ({ size = 20, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
  </svg>
);

// Added className and size support to CloseIcon to resolve TypeScript errors
export const CloseIcon = ({ size = 24, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// Added className and size support to UserIcon to fix IntrinsicAttributes error in App.tsx
export const UserIcon = ({ size = 24, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

// Added size and className props to TrashIcon to fix the error in App.tsx
export const TrashIcon = ({ size = 20, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    <line x1="10" y1="11" x2="10" y2="17"></line>
    <line x1="14" y1="11" x2="14" y2="17"></line>
  </svg>
);

export const QRIcon = ({ size = 20, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
    <path d="M7 7h.01"></path>
    <path d="M17 7h.01"></path>
    <path d="M7 17h.01"></path>
    <path d="M17 17h.01"></path>
  </svg>
);

export const CameraIcon = ({ size = 20, className = "" }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
    <circle cx="12" cy="13" r="4"></circle>
  </svg>
);
